import { UserRole } from '../../../types/UserRole';
import { UserType } from '../../../types/UserType';
import { Select, SelectProps } from '../../cdl/Select/Select';
import { translate } from '../../common/helpers/translate.helper';
import { useEnums } from '../../common/hooks/useEnums';
import { useRole } from '../../common/hooks/useRole';
import { IconInfoCircle } from '../../common/icons/cdl/InfoCircle';

interface RoleSelectOption {
    label: string;
    value: string;
}

export interface RoleSelectProps extends Omit<SelectProps<RoleSelectOption>, 'value' | 'onChange' | 'options'> {
    value: string;
    onChange: (newRole: string | undefined) => void;
    userType?: UserType;
}

export const RoleSelect = (props: RoleSelectProps) => {
    const { getHumanReadableValue } = useEnums();
    const role = useRole();

    const options: RoleSelectOption[] = Object.values(UserRole)
        .filter((it) => {
            if (it === UserRole.CUSTOM) {
                return false;
            }

            if (!role.isSuperAdmin() && role.accessLevel !== 'ADMIN') {
                return it !== UserRole.GROUP_ADMIN;
            }

            return true;
        })

        .map((it) => {
            return {
                label: getHumanReadableValue('role', it),
                value: it,
            };
        });

    const value = { value: props.value, label: getHumanReadableValue('role', props.value) };

    const userRoleWithoutCustom = props.value as Exclude<UserRole, UserRole.CUSTOM>;

    const getCaption = () => {
        if (props.caption) {
            return props.caption;
        }

        if (!userRoleWithoutCustom) {
            return undefined;
        }

        if (userRoleWithoutCustom === UserRole.COMPANY_EMPLOYEE) {
            if (props.userType === UserType.SUPPLIER) {
                return translate('user.userroleexplanation.COMPANY_EMPLOYEE.SUPPLIER');
            }
            if (props.userType === UserType.CUSTOMER) {
                return translate('user.userroleexplanation.COMPANY_EMPLOYEE.CUSTOMER');
            }
        }

        return translate(`user.userroleexplanation.${userRoleWithoutCustom}` as any);
    };

    return (
        <Select<RoleSelectOption>
            {...props}
            options={options}
            onChange={(newValue) => {
                props.onChange(newValue?.value);
            }}
            value={value}
            CaptionIcon={props.caption ? undefined : IconInfoCircle}
            caption={getCaption()}
        />
    );
};
